/**
 * Plain-English summary of a PRE decision for display at the top of the
 * expanded decision card. Translates the canonical codes (CHK_*, COND_*, RISK_*)
 * into something a non-engineer admin can scan in 2 seconds.
 */
import type { PREDecisionLog } from '@respectly/shared-types';

interface DecisionSummary {
  /** Short emoji-prefixed verdict, e.g. "✅ Allowed" */
  verdict: string;
  /** One-sentence rationale grounded in the actual rules / conditions / risk flags. */
  detail: string;
  /** Tone hint for the rendering layer. */
  tone: 'success' | 'warning' | 'error';
}

const FRIENDLY_RULE_NAMES: Record<string, string> = {
  CHK_INTERNAL_DNC_CLEAR: 'internal Do-Not-Contact list',
  CHK_NATIONAL_REGISTRY_SCRUBBED: 'national Do-Not-Call registry',
  CHK_NATIONAL_REGISTRY_CLEAR: 'national Do-Not-Call registry',
  CHK_WITHIN_CALLING_HOURS: 'recipient calling hours',
  CHK_FREQUENCY_CAP_24H_OK: '24-hour frequency cap',
  CHK_FREQUENCY_CAP_7D_OK: '7-day frequency cap',
  CHK_CHANNEL_PREFERENCE_MATCH: 'contact channel preference',
  CHK_CONTACT_ELIGIBLE: 'contact eligibility',
  CHK_CONSENT_AT_LEAST: 'consent level',
  CHK_WIRELESS_MARKETING_CONSENT_OK: 'TCPA wireless marketing consent',
  CHK_SENDER_ID_AND_UNSUB_PRESENT: 'sender ID + unsubscribe',
  CHK_REVOCATION_HANDLING_ENABLED: 'opt-out handling',
  CHK_AUTOMATION_ALLOWED_FOR_SEND: 'automation policy',
  CHK_END_CALL_WITHIN_10S_ON_REQUEST: '10-second end-call requirement',
  CHK_DNC_DISCLOSURE_AT_CALL_START: 'DNC disclosure at call start',
  CHK_CASL_CONSENT_VALID: 'CASL consent (Canada)',
  CHK_CASL_CONSPICUOUS_PUBLICATION_VALID: 'CASL conspicuous publication',
  CHK_LGPD_LAWFUL_BASIS_VALID: 'LGPD lawful basis (Brazil)',
  CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR: 'Brazil "Não Me Perturbe" registry',
  CHK_BRAZIL_0303_PREFIX: 'Brazil 0303 prefix requirement',
  CHK_RIGHT_TO_DISCONNECT_OK: 'right-to-disconnect window',
};

const FRIENDLY_CONDITION: Record<string, string> = {
  COND_SCHEDULE_WITHIN_HOURS:
    "is held and will send automatically during the recipient's local calling hours",
  COND_REGISTRY_SCRUB_REQUIRED: 'must be scrubbed against the national registry',
  COND_INCLUDE_SENDER_ID_AND_UNSUB: 'must include sender ID and an unsubscribe option',
  COND_RECORD_RETENTION: 'requires record retention',
  COND_CAPTURE_EVIDENCE_SNAPSHOT: 'requires an evidence snapshot',
  COND_VERIFY_RELEVANCE_RATIONALE: 'requires a relevance rationale',
};

const FRIENDLY_RISK: Record<string, string> = {
  RISK_UNKNOWN_JURISDICTION: 'unknown jurisdiction',
  RISK_UNKNOWN_LINE_TYPE: 'unknown line type',
  RISK_UNKNOWN_NATIONAL_REGISTRY_STATUS: 'unknown national registry status',
  RISK_UNKNOWN_INTERNAL_DNC_STATUS: 'unknown internal DNC status',
  RISK_STATE_ASSUMED_BY_AREA_CODE: 'state inferred from area code',
  RISK_CHANNEL_FALLBACK_TO_SMS: 'channel fell back to SMS',
  RISK_SMS_B2B_NORMALIZED_TO_B2C: 'B2B normalized to B2C for SMS',
};

function friendlyRule(ruleId: string, fallback: string): string {
  return FRIENDLY_RULE_NAMES[ruleId] ?? fallback ?? ruleId;
}

function friendlyCondition(code: string): string {
  return FRIENDLY_CONDITION[code] ?? code.replace(/^COND_/, '').replace(/_/g, ' ').toLowerCase();
}

function friendlyRisk(code: string): string {
  return FRIENDLY_RISK[code] ?? code.replace(/^RISK_/, '').replace(/_/g, ' ').toLowerCase();
}

function channelLabel(log: PREDecisionLog): string {
  switch (log.channel) {
    case 'SMS': return 'SMS';
    case 'EMAIL': return 'email';
    case 'VOICE': return 'voice call';
    case 'RCS': return 'RCS message';
    case 'SOCIAL_MESSAGE': return 'social message';
    default: return String(log.channel).toLowerCase();
  }
}

function purposeLabel(log: PREDecisionLog): string {
  return log.messagePurpose === 'MARKETING' ? 'marketing' : 'informational';
}

export function summarizeDecision(log: PREDecisionLog): DecisionSummary {
  const channel = channelLabel(log);
  const purpose = purposeLabel(log);

  // BLOCK — find the first failed rule for the reason
  if (log.decision === 'BLOCK') {
    const firstFail = (log.output.trace ?? []).find((t) => t.result === 'FAIL');
    const reason = firstFail
      ? `failed the ${friendlyRule(firstFail.ruleId, firstFail.ruleName)} check`
      : log.eligibility === 'NOT_ELIGIBLE'
      ? 'the contact is not eligible for this channel'
      : 'one or more policy rules denied the send';
    return {
      verdict: 'Blocked',
      detail: `This ${purpose} ${channel} was blocked because ${reason}.`,
      tone: 'error',
    };
  }

  // ALLOW_WITH_CONDITIONS — describe the conditions
  if (log.decision === 'ALLOW_WITH_CONDITIONS') {
    const conds = (log.output.conditions ?? []).slice(0, 2).map((c) => friendlyCondition(c.code));
    const condStr =
      conds.length === 0
        ? 'with attached conditions'
        : conds.length === 1
        ? conds[0]
        : `${conds[0]}, and ${conds[1]}`;
    return {
      verdict: 'Allowed with conditions',
      detail: `This ${purpose} ${channel} is permitted but ${condStr}.`,
      tone: 'warning',
    };
  }

  // ALLOW — note any risk flags worth surfacing
  const risks = (log.output.riskFlags ?? []).slice(0, 2).map((r) => friendlyRisk(r.code));
  const tail =
    risks.length === 0
      ? `All consent and policy checks passed for ${log.jurisdictionCode}.`
      : `Note: ${risks.join('; ')}.`;
  return {
    verdict: 'Allowed',
    detail: `This ${purpose} ${channel} cleared every applicable rule. ${tail}`,
    tone: 'success',
  };
}
